"use client";

import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { useVoiceDeviceContext } from "./VoiceDeviceProvider";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useToast } from "@/app/_module/hooks/use-toast";
import { removeRealtimeChannel } from "../services/realtimeTeardown";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";

interface IncomingCall {
    id: string;
    callerName: string;
    callerNumber: string;
    leadId?: string;
    timestamp: Date;
    isRinging: boolean;
    callSid: string;
    conferenceName: string;
    isPooledLead?: boolean; // True if this is an unassigned pooled lead
    isLiveTransfer?: boolean; // Live Transfer campaign ring — changes the widget label only
    campaignName?: string;
    sourceLabel?: string; // TV line's tag (e.g. "TV Ad"), shown on the ring
    phoneOwnerId?: string; // Original phone number owner (for pooled calls)
}

/**
 * A previously-active call that has been placed on hold while the agent moves to
 * the new incoming call. Captured when `answerCall` is invoked with `holdActive: true`.
 * Resuming it puts the *current* call on hold and reconnects the agent's Twilio
 * device to this held call's conference.
 */
interface HeldCall {
    callSid: string;
    conferenceName: string;
    callerName: string;
    callerNumber: string;
    leadId?: string;
    heldAt: Date;
    /** True when the held call belonged to a MULTI-LINE session — stamped at
     *  HOLD time because the incoming call's connect() clears the mode refs,
     *  so reading them at resume time always said 'manual'. */
    fromMultiLine?: boolean;
}

/**
 * Set when an agency owner / admin starts a whisper or barge coach
 * session on the agent's call. Drives the persistent banner pill in
 * the agent's Active Call UI so they know they're being coached.
 * Listen mode is silent by design — never sets this state.
 */
interface CoachActiveState {
    mode: 'whisper' | 'barge';
    managerName: string;
    callSid: string | null;
    joinedAt: Date;
}

interface IncomingCallContextType {
    currentCall: IncomingCall | null;
    isCallVisible: boolean;
    /** True when an incoming call arrived while the user was already on an active call */
    isCallWaiting: boolean;
    /** The previously-active call that the agent placed on hold via "Hold & Answer". */
    heldCall: HeldCall | null;
    /** True if the active call was merged into a 3-way conference via "Merge". */
    isMergedCall: boolean;
    /** Supervisor whisper / barge state — drives agent-side coach banner. */
    coachActive: CoachActiveState | null;
    showIncomingCall: (call: IncomingCall) => void;
    hideIncomingCall: () => void;
    answerCall: (callId: string, options?: { holdActive?: boolean; mergeActive?: boolean }) => void;
    declineCall: (callId: string) => void;
    dismissCall: () => void;
    /** Swap: put current call on hold, resume the previously-held call. */
    resumeHeldCall: () => Promise<void>;
    /** Clear held-call state (e.g., after the held call drops or user dismisses). */
    clearHeldCall: () => void;
}

const IncomingCallContext = createContext<IncomingCallContextType | undefined>(undefined);

interface IncomingCallProviderProps {
    children: ReactNode;
    userId?: string;
}

// LocalStorage key for auto-open lead details setting (shared with UnifiedDialerWidget)
const AUTO_OPEN_LEAD_DETAILS_KEY = 'dialer_auto_open_lead_details';

export const IncomingCallProvider: React.FC<IncomingCallProviderProps> = ({ children, userId }) => {
    const [currentCall, setCurrentCall] = useState<IncomingCall | null>(null);
  // Guarded rejoin: exponential backoff + a hard attempt cap. Without the cap a
  // permanently failing channel re-subscribes in a tight loop and hangs the tab.
  const { rejoinToken, handleStatus } = useRealtimeRejoin("incoming-call");
    const [isCallVisible, setIsCallVisible] = useState(false);
    const [heldCall, setHeldCall] = useState<HeldCall | null>(null);
    const [isMergedCall, setIsMergedCall] = useState(false);
    const [coachActive, setCoachActive] = useState<CoachActiveState | null>(null);
    const {
        connect,
        disconnect,
        stopRingback,
        activeCallSid,
        callMetadata,
        currentConferenceName,
        callStatus,
        refreshConferenceParticipants,
        setActiveConference,
        // Handler functions for Supabase events (delegated to TwilioDeviceContext)
        handleCustomerDisconnected,
        handleParticipantConnected,
        handleParticipantLeft,
        handleCallStatusUpdated,
        handlePhoneAnswered,
        handleCustomerAnswered,
        isInMultiLineMode,
        isInPowerDialerMode,
        setIsInMultiLineMode,
    } = useVoiceDeviceContext();
    // Get user status to check if user is on_call
    const { user } = useUserContext();
    const userStatus = (user?.user as any)?.userStatus;
    const { toast } = useToast();
    const router = useRouter();
    const ringtoneRef = useRef<HTMLAudioElement | null>(null);
    const supabaseChannelRef = useRef<any>(null);
    // Ref to track current call for use in Supabase callbacks (avoids stale closure)
    const currentCallRef = useRef<IncomingCall | null>(null);
    // Ref to track if user is already on an active call (to suppress incoming call notifications)
    const activeCallSidRef = useRef<string | null>(null);
    // Ref to track user status (on_call) to suppress incoming call notifications
    const userStatusRef = useRef<string | undefined>(undefined);
    // Refs to track dialer session modes (to suppress incoming call notifications between calls/batches)
    const isInMultiLineModeRef = useRef(false);
    const isInPowerDialerModeRef = useRef(false);
    // Track answered/declined call SIDs to prevent re-showing after user action
    const handledCallSidsRef = useRef<Set<string>>(new Set());
    // Refs to avoid stale closures in Supabase callbacks
    const stopRingbackRef = useRef(stopRingback);
    const handleCustomerDisconnectedRef = useRef(handleCustomerDisconnected);
    const handleParticipantConnectedRef = useRef(handleParticipantConnected);
    const handleParticipantLeftRef = useRef(handleParticipantLeft);
    const handleCallStatusUpdatedRef = useRef(handleCallStatusUpdated);
    const handlePhoneAnsweredRef = useRef(handlePhoneAnswered);
    const handleCustomerAnsweredRef = useRef(handleCustomerAnswered);

    // Keep ref in sync with state
    useEffect(() => {
        currentCallRef.current = currentCall;
    }, [currentCall]);

    // Keep activeCallSidRef in sync with state (to check if user is on an active call)
    useEffect(() => {
        activeCallSidRef.current = activeCallSid;
    }, [activeCallSid]);

    // Keep userStatusRef in sync with state (to check if user is on_call)
    useEffect(() => {
        userStatusRef.current = userStatus;
    }, [userStatus]);

    // Keep dialer mode refs in sync with state (to suppress incoming calls during dialer sessions)
    useEffect(() => {
        isInMultiLineModeRef.current = isInMultiLineMode;
    }, [isInMultiLineMode]);
    useEffect(() => {
        isInPowerDialerModeRef.current = isInPowerDialerMode;
    }, [isInPowerDialerMode]);

    // Keep handler refs in sync
    useEffect(() => {
        stopRingbackRef.current = stopRingback;
        handleCustomerDisconnectedRef.current = handleCustomerDisconnected;
        handleParticipantConnectedRef.current = handleParticipantConnected;
        handleParticipantLeftRef.current = handleParticipantLeft;
        handleCallStatusUpdatedRef.current = handleCallStatusUpdated;
        handlePhoneAnsweredRef.current = handlePhoneAnswered;
        handleCustomerAnsweredRef.current = handleCustomerAnswered;
    }, [stopRingback, handleCustomerDisconnected, handleParticipantConnected, handleParticipantLeft, handleCallStatusUpdated, handlePhoneAnswered, handleCustomerAnswered]);

    // Track if audio has been "unlocked" by user interaction
    const audioUnlockedRef = useRef<boolean>(false);
    // Safety timeout for the ringtone — parallel to the outbound ringback
    // timeout. Without it, a dropped `call_ended` realtime broadcast would
    // leave the ringtone playing forever. 45s covers a real ring window +
    // propagation headroom; Twilio's default inbound dial timeout is 30s.
    const ringtoneTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Initialize ringtone audio
    useEffect(() => {
        // Create ringtone audio element - randomly select between two startup sounds
        const initAudio = async () => {
            try {
                // Randomly select between the two startup sounds
                const soundFiles = ["/sounds/a_startup_2.mp3", "/sounds/a_startup_3.mp3"];
                const randomSound = soundFiles[Math.floor(Math.random() * soundFiles.length)];

                const audio = new Audio(randomSound);

                // Add error handler in case file doesn't load
                audio.addEventListener('error', () => {
                    // Try the other sound as fallback
                    const fallbackSound = soundFiles.find(s => s !== randomSound);
                    if (fallbackSound) {
                        const fallbackAudio = new Audio(fallbackSound);
                        fallbackAudio.loop = true;
                        // Don't let OS/browser media controls resume the ringtone.
                        (fallbackAudio as any).disableRemotePlayback = true;
                        fallbackAudio.addEventListener('error', () => {
                            // Silently fail if no audio files available
                        });
                        ringtoneRef.current = fallbackAudio;
                    }
                });

                audio.loop = true;
                // Don't let OS/browser media controls resume the ringtone
                // (media keys are already globally no-op'd by TwilioDeviceContext,
                // this is belt-and-suspenders for remote-playback UIs).
                (audio as any).disableRemotePlayback = true;
                ringtoneRef.current = audio;
            } catch (error) {
                // Silently fail if initialization fails
            }
        };

        initAudio();

        return () => {
            if (ringtoneRef.current) {
                ringtoneRef.current.pause();
                ringtoneRef.current = null;
            }
            if (ringtoneTimeoutRef.current) {
                clearTimeout(ringtoneTimeoutRef.current);
                ringtoneTimeoutRef.current = null;
            }
        };
    }, []);

    // Local fallback: Twilio SDK fires `cancel` on the incoming Call when the
    // caller hangs up before the agent accepts. TwilioDeviceContext bridges
    // that to this window event. Purely local — no Supabase hop — so it
    // works even when realtime has dropped the `call_ended` broadcast.
    useEffect(() => {
        const onIncomingCanceled = () => {
            stopRingtoneRef.current();
            hideIncomingCallRef.current();
        };
        window.addEventListener('twilio:incoming-canceled', onIncomingCanceled);
        return () => window.removeEventListener('twilio:incoming-canceled', onIncomingCanceled);
    }, []);

    // Unlock audio on first user interaction (required by browser autoplay policy)
    useEffect(() => {
        const unlockAudio = () => {
            if (audioUnlockedRef.current || !ringtoneRef.current) return;
            // Native shell: Capacitor's WKWebView allows programmatic audio
            // playback with no prior gesture, so the browser-autoplay unlock
            // is pointless here — and it was the source of the occasional
            // audible 1-2s ringtone blip after login (proven via play()
            // instrumentation: the ONLY login-time play was this unlock).
            try {
                if ((window as any).Capacitor?.isNativePlatform?.()) {
                    audioUnlockedRef.current = true;
                    return;
                }
            } catch { /* fall through to the web unlock */ }

            // Play and immediately pause to "unlock" audio for future playback.
            // MUST use `muted`, not `volume = 0`: on iOS the volume property is
            // READ-ONLY (hardware-controlled), so the "silent" unlock played the
            // ringtone out loud for ~1-2s on the first tap after login in the
            // native app. `muted` is honored everywhere.
            const audio = ringtoneRef.current;
            audio.muted = true;
            audio.play().then(() => {
                audio.pause();
                audio.currentTime = 0;
                audio.muted = false;
                audioUnlockedRef.current = true;
            }).catch(() => {
                // Still blocked, will try again on next interaction
                audio.muted = false;
            });
        };

        // Listen for user interactions to unlock audio
        const events = ['click', 'touchstart', 'keydown'];
        events.forEach(event => document.addEventListener(event, unlockAudio, { once: false, passive: true }));

        return () => {
            events.forEach(event => document.removeEventListener(event, unlockAudio));
        };
    }, []);

    // Play ringtone
    const playRingtone = useCallback(() => {
        if (!ringtoneRef.current) {
            console.warn('[IncomingCall] Ringtone ref is null, cannot play');
            return;
        }

        // CALL-WAITING CHIME MODE: if the agent is already on an active call,
        // don't blast the full looping ringtone in their ear mid-pitch.
        // Matches FollowUpBoss / iPhone call-waiting UX — single short chime
        // at reduced volume, no loop. Visual widget still pops with
        // Hold & Answer / Merge / Decline so the agent can act.
        const isAgentBusy = !!activeCallSidRef.current;
        const audio = ringtoneRef.current;

        if (isAgentBusy) {
            audio.loop = false;
            audio.volume = 0.25; // Quiet — they're talking to someone
            audio.currentTime = 0;
            audio.play().catch(() => {});
            // Kill after a short chime window so it doesn't drone through
            // the full sound file. Feels like a notification ping.
            if (ringtoneTimeoutRef.current) clearTimeout(ringtoneTimeoutRef.current);
            ringtoneTimeoutRef.current = setTimeout(() => {
                if (ringtoneRef.current) {
                    ringtoneRef.current.pause();
                    ringtoneRef.current.currentTime = 0;
                    // Restore loop / volume for the next idle-state ringtone.
                    ringtoneRef.current.loop = true;
                    ringtoneRef.current.volume = 1;
                }
                ringtoneTimeoutRef.current = null;
            }, 800);
            return;
        }

        // Normal idle ringtone: loop at full volume.
        audio.loop = true;
        audio.volume = 1;
        audio.play().catch((error) => {
            console.warn('[IncomingCall] Failed to play ringtone:', error.message);
            // If autoplay is blocked and we haven't unlocked yet, the audio will remain silent
            // The unlock mechanism should handle this on next user interaction
        });
        // Safety timeout: auto-stop after 45s in case the `call_ended`
        // realtime broadcast never arrives (dropped websocket packet,
        // backgrounded tab, Supabase hiccup). Matches the resilience
        // stack the outbound ringback has — a locally-owned ceiling
        // that can't be missed by the network.
        if (ringtoneTimeoutRef.current) {
            clearTimeout(ringtoneTimeoutRef.current);
        }
        ringtoneTimeoutRef.current = setTimeout(() => {
            if (ringtoneRef.current) {
                ringtoneRef.current.pause();
                ringtoneRef.current.currentTime = 0;
            }
            ringtoneTimeoutRef.current = null;
        }, 45000);
    }, []);

    // Stop ringtone
    const stopRingtone = useCallback(() => {
        if (ringtoneTimeoutRef.current) {
            clearTimeout(ringtoneTimeoutRef.current);
            ringtoneTimeoutRef.current = null;
        }
        if (ringtoneRef.current) {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
        }
        if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
            try {
                navigator.mediaSession.playbackState = 'none';
            } catch {}
        }
    }, []);

    // Refs for callbacks to avoid stale closures in Supabase listener
    const showIncomingCallRef = useRef<(call: IncomingCall) => void>(() => {});
    const playRingtoneRef = useRef<() => void>(() => {});
    const stopRingtoneRef = useRef<() => void>(() => {});
    const hideIncomingCallRef = useRef<() => void>(() => {});

    // Keep callback refs updated
    useEffect(() => {
        showIncomingCallRef.current = (call: IncomingCall) => {
            setCurrentCall(call);
            setIsCallVisible(true);
        };
        hideIncomingCallRef.current = () => {
            setCurrentCall(null);
            setIsCallVisible(false);
            stopRingtone();
        };
        playRingtoneRef.current = playRingtone;
        stopRingtoneRef.current = stopRingtone;
    }, [playRingtone, stopRingtone]);

    // Setup Supabase Realtime listener for incoming calls
    useEffect(() => {
        if (!userId) return;

        const setupRealtimeListener = async () => {
            try {
                const supabase = await getSupabaseClient();

                // Subscribe to incoming call events for this user
                const channel = supabase
                    .channel(`incoming_calls:${userId}`)
                    .on('broadcast', { event: 'incoming_call' }, (payload: any) => {
                        const { callSid, conferenceName, callerNumber, callerName, leadId, isPooledLead, phoneOwnerId, isLiveTransfer, campaignName, sourceLabel, sentAt } = payload.payload;

                        // STALE-REPLAY GUARD: when the app was backgrounded
                        // (iOS suspends the websocket), the channel can deliver
                        // a queued ring on resume — often followed instantly by
                        // its call_ended — producing random 1-2s ghost ringtone
                        // bursts for calls that are long over. A ring older
                        // than 30s can't still be ringing (Twilio would have
                        // gone to voicemail); drop it silently.
                        if (typeof sentAt === 'number' && Date.now() - sentAt > 30_000) {
                            return;
                        }

                        // Skip if this call has already been answered or declined
                        if (handledCallSidsRef.current.has(callSid)) {
                            return;
                        }

                        // Always show the incoming widget + play ringtone. The agent decides what to do
                        // with it based on their current context: decline to keep dialing, hold & answer
                        // to pause the active line and take the new call, or merge into a 3-way. This
                        // used to silently drop incoming calls during multi-line / power dialer sessions
                        // — agents complained they were losing inbound calls while prospecting.
                        // The `isCallWaiting` flag (derived from activeCallSid) controls which button set
                        // the widget renders, so dialer sessions with a connected line get the full
                        // hold/merge/decline UX, and sessions that are still ringing get plain
                        // Answer/Decline (since there's nothing meaningful to hold yet).

                        // If we're already showing this call, UPDATE it with new data if it has more info
                        // This handles the race-to-answer flow where first broadcast has no lead data,
                        // then second broadcast (when caller joins conference) has leadId and isPooledLead
                        const existingCall = currentCallRef.current;
                        if (existingCall && existingCall.callSid === callSid) {
                            // Check if new broadcast has lead info that current one doesn't
                            const hasNewLeadInfo = leadId && !existingCall.leadId;
                            const hasNewPooledInfo = isPooledLead && !existingCall.isPooledLead;

                            if (hasNewLeadInfo || hasNewPooledInfo) {
                                // Update the current call with new information
                                const updatedCall: IncomingCall = {
                                    ...existingCall,
                                    leadId: leadId || existingCall.leadId,
                                    callerName: callerName || existingCall.callerName,
                                    isPooledLead: isPooledLead || existingCall.isPooledLead,
                                    isLiveTransfer: isLiveTransfer || existingCall.isLiveTransfer,
                                    campaignName: campaignName || existingCall.campaignName,
                                    sourceLabel: sourceLabel || existingCall.sourceLabel,
                                    phoneOwnerId: phoneOwnerId || existingCall.phoneOwnerId,
                                };
                                setCurrentCall(updatedCall);
                            }
                            return;
                        }

                        // Show incoming call notification
                        const incomingCall: IncomingCall = {
                            id: callSid,
                            callSid,
                            conferenceName,
                            // Leave empty when there's no matched lead name — the
                            // widget then shows the phone NUMBER as the headline
                            // instead of a generic "Unknown Caller".
                            callerName: callerName || "",
                            callerNumber: callerNumber || "",
                            leadId,
                            timestamp: new Date(),
                            isRinging: true,
                            isPooledLead: isPooledLead || false,
                            isLiveTransfer: isLiveTransfer || false,
                            campaignName,
                            sourceLabel,
                            phoneOwnerId,
                        };

                        // Use refs to avoid stale closure
                        showIncomingCallRef.current(incomingCall);
                        playRingtoneRef.current();
                    })
                    .on('broadcast', { event: 'call_ended' }, (payload: any) => {
                        const { callSid } = payload.payload;

                        // If this is the current incoming call, stop ringing and hide widget
                        // Use ref to avoid stale closure issue
                        if (currentCallRef.current?.callSid === callSid) {
                            stopRingtoneRef.current();
                            hideIncomingCallRef.current();
                        }

                        // ...and the HELD call, if that's the one that hung up. This used
                        // to be a second .on('call_ended') attached to this channel AFTER
                        // it was already subscribed. supabase-js 2.101 made late .on()
                        // registration throw for postgres_changes/presence, so that pattern
                        // was a live landmine for any upgrade. One handler, one event.
                        if (callSid) heldCallEndedRef.current(callSid);
                    })
                    // Listen for customer_answered - stop outbound ringback and update call status
                    .on('broadcast', { event: 'customer_answered' }, (payload: any) => {
                        // Stop the outbound ringback tone immediately
                        if (stopRingbackRef.current) {
                            stopRingbackRef.current();
                        }
                        // Delegate to TwilioDeviceContext to transition callStatus from "ringing" to "in-progress"
                        if (handleCustomerAnsweredRef.current) {
                            handleCustomerAnsweredRef.current(payload.payload);
                        }
                    })
                    // Listen for customer_disconnected - dismiss incoming widget if caller hung up before agent answered
                    .on('broadcast', { event: 'customer_disconnected' }, (payload: any) => {
                        const { parentCallSid } = payload.payload;

                        // If this is the current incoming call (agent hasn't answered yet), dismiss the widget
                        if (currentCallRef.current?.callSid === parentCallSid) {
                            stopRingtoneRef.current();
                            hideIncomingCallRef.current();
                        }

                        // Also delegate to TwilioDeviceContext for active call cleanup
                        handleCustomerDisconnectedRef.current(payload.payload);
                    })
                    // Listen for participant_connected - delegate to TwilioDeviceContext handler
                    .on('broadcast', { event: 'participant_connected' }, (payload: any) => {
                        handleParticipantConnectedRef.current(payload.payload);
                    })
                    // LIVE TRANSFER (2026-07-23): relay to TwilioDeviceContext /
                    // UnifiedDialerWidget via window events (established cross-
                    // context pattern — same as coach:barge-ended).
                    .on('broadcast', { event: 'transfer_offer' }, (payload: any) => {
                        try { window.dispatchEvent(new CustomEvent('transfer:offer', { detail: payload.payload })); } catch {}
                    })
                    .on('broadcast', { event: 'transfer_status' }, (payload: any) => {
                        try { window.dispatchEvent(new CustomEvent('transfer:status', { detail: payload.payload })); } catch {}
                    })
                    // Listen for participant_left - delegate to TwilioDeviceContext handler
                    .on('broadcast', { event: 'participant_left' }, (payload: any) => {
                        handleParticipantLeftRef.current(payload.payload);
                    })
                    // Listen for call_status_updated - delegate to TwilioDeviceContext handler
                    .on('broadcast', { event: 'call_status_updated' }, (payload: any) => {
                        handleCallStatusUpdatedRef.current(payload.payload);
                    })
                    // Listen for phone_answered - delegate to TwilioDeviceContext handler
                    .on('broadcast', { event: 'phone_answered' }, (payload: any) => {
                        handlePhoneAnsweredRef.current(payload.payload);
                    })
                    // Compliance: notify agent when supervisor whispers / barges.
                    // Listen mode is silent by design — only whisper / barge
                    // events arrive here. Show a non-blocking toast AND a
                    // persistent banner pill in the active call widget so
                    // the agent knows their call is being coached for as
                    // long as the supervisor is connected.
                    .on('broadcast', { event: 'coach_joined' }, (payload: any) => {
                        const { mode, managerName, callSid } = payload?.payload || {};
                        if (!mode) return;
                        setCoachActive({
                            mode,
                            managerName: managerName || 'Supervisor',
                            callSid: callSid || null,
                            joinedAt: new Date(),
                        });
                        toast({
                            title: mode === 'barge' ? 'Supervisor on the call' : 'Supervisor whispering',
                            description: mode === 'barge'
                                ? `${managerName || 'Your supervisor'} has joined and the lead can hear them.`
                                : `${managerName || 'Your supervisor'} is coaching you (lead can't hear).`,
                            duration: 6000,
                        });
                    })
                    // Supervisor stopped whisper / barge — clear the banner.
                    .on('broadcast', { event: 'coach_left' }, () => {
                        setCoachActive(null);
                    })
                    .subscribe(handleStatus);

                supabaseChannelRef.current = channel;
            } catch (error) {
                // Silently fail - connection issues will be retried
            }
        };

        setupRealtimeListener();

        // Cleanup on unmount
        return () => {
            if (supabaseChannelRef.current) {
                removeRealtimeChannel(supabaseChannelRef.current);
                supabaseChannelRef.current = null;
            }
        };
    }, [userId, rejoinToken]);

    const showIncomingCall = useCallback((call: IncomingCall) => {
        setCurrentCall(call);
        setIsCallVisible(true);
    }, []);

    const hideIncomingCall = useCallback(() => {
        setCurrentCall(null);
        setIsCallVisible(false);
        stopRingtone();
    }, [stopRingtone]);

    const answerCall = useCallback(async (callId: string, options?: { holdActive?: boolean; mergeActive?: boolean }) => {
        // Captured at ENTRY — everything below (pause dispatch, connect) can
        // mutate the mode refs before the heldCall record is written.
        const answerWasMultiLine = isInMultiLineModeRef.current;
        if (!currentCall) {
            return;
        }

        // Capture call details before hiding (since hideIncomingCall clears currentCall)
        const callDetails = {
            conferenceName: currentCall.conferenceName,
            leadId: currentCall.leadId,
            callSid: currentCall.callSid,
            callerName: currentCall.callerName,
            callerNumber: currentCall.callerNumber,
            isPooledLead: currentCall.isPooledLead,
            isLiveTransfer: currentCall.isLiveTransfer,
            campaignName: currentCall.campaignName,
            phoneOwnerId: currentCall.phoneOwnerId,
        };

        // Mark this call as handled so it won't be re-shown if broadcast arrives again
        handledCallSidsRef.current.add(callDetails.callSid);
        // Prevent unbounded growth — keep only the most recent 50 entries
        if (handledCallSidsRef.current.size > 100) {
            const entries = Array.from(handledCallSidsRef.current);
            handledCallSidsRef.current = new Set(entries.slice(-50));
        }

        // LIVE TRANSFER RACE REFEREE: claim the answer BEFORE touching the
        // dialer, the widget, or the conference audio. Exactly one agent per
        // blast call wins the atomic DB claim (claimed_by_user_id flips from
        // NULL once); everyone else dismisses quietly instead of creating a
        // 3-way party line or overwriting the winner's lead. Fail-open: if
        // the referee is unreachable, answering proceeds — a broken claim
        // endpoint must never make calls unanswerable.
        if (callDetails.isLiveTransfer && callDetails.callSid) {
            let won = true;
            let reason: string | undefined;
            try {
                const claimRes = await api.post({
                    url: getApiUrl('/lead-assignment/live-transfer-claim'),
                    body: { callSid: callDetails.callSid },
                    auth: true,
                });
                let v: any = claimRes?.data;
                for (let i = 0; i < 3 && v && typeof v === 'object' && 'data' in v; i++) v = v.data;
                if (v && typeof v.won === 'boolean') won = v.won;
                if (v && typeof v.reason === 'string') reason = v.reason;
            } catch {
                /* referee unreachable — fail open, proceed to answer */
            }
            if (!won) {
                stopRingtone();
                hideIncomingCall();
                toast(reason === 'ended'
                    ? { title: 'Caller already hung up', description: 'They left before the call was answered' }
                    : { title: 'Call already taken', description: 'Answered by someone else' });
                return;
            }
        }

        // Stop ringtone and hide the widget IMMEDIATELY when user clicks answer
        // Don't wait for connect() - user has made their decision
        stopRingtone();
        hideIncomingCall();

        // EMERGENCY SESSION PAUSE: if the agent was mid multi-line or power dialer
        // session, pause it cleanly (same behavior as clicking "Call John" from a
        // lead detail page — session stays intact, agent can resume after the
        // incoming call ends). Uses the same handlePause the agent would click
        // manually. Without this, loser lines keep ringing server-side during the
        // inbound call and can create a conference with no one to talk to.
        //
        // SKIP for Merge (flipped): the agent's SDK call never disconnects — the
        // incoming caller is redirected INTO the active conference and the agent
        // continues on the same call. Pausing here would leave the dialer frozen
        // when the merged call eventually ends, and a manual Resume would re-dial
        // the current lead (already done) instead of advancing to the next one.
        try {
        // (try opened HERE — Mark Gurraj 2026-07-22: the emergency-pause dispatch +
        // hold/merge handoff below previously ran OUTSIDE the try. Any throw in
        // that region escaped as an unhandled rejection: guards stayed set,
        // the dialer stayed frozen, and the incoming call rang out to no_answer
        // with no recovery. Now every failure path lands in the catch, which
        // resets the guards and emergency-resumes the dialer.)
        if (!options?.mergeActive) {
            if (isInMultiLineModeRef.current || isInPowerDialerModeRef.current) {
                // Set the call-waiting guard BEFORE dispatching emergency-pause so
                // backend handlers see it consistently. Previously this flag was
                // only set when priorCallSid + holdActive/mergeActive ran below —
                // meaning plain "Answer" during an active dialer session never
                // triggered the auto-resume on call-end (Mark's bug: 4-5s "caller
                // hung up" because backend kept dialing batches and the agent's
                // SDK got reconnected to a new race conference, dropping the
                // incoming customer mid-handoff).
                try { sessionStorage.setItem('__callwaiting_active', '1'); } catch {}

                // CRITICAL: lock the auto-resume / flag-clear effect for the
                // duration of the SDK transition. When connect() runs below
                // for the incoming conference, Twilio's SDK first disconnects
                // the OLD multi-line race SDK leg. That fires call.on(
                // "disconnect") which sets activeCallSid=null. The
                // prevActiveCallSidRef effect at line ~1029 sees
                // `prev && !activeCallSid && !heldCall`, treats this as
                // "active call ended", CLEARS __callwaiting_active, and
                // dispatches multiline:emergency-resume. That un-pauses the
                // multi-line dialer, dialNextLeads() fires disconnect() for
                // the new batch, the call-waiting guard in disconnect() is
                // already cleared, and the cancel POST (reason=agent_hangup)
                // kills Mark's brand-new incoming call ~13s in.
                //
                // Setting this ref at acceptCall start makes the auto-resume
                // effect early-return on the brief activeCallSid=null window
                // during the SDK swap. We clear it once connect() resolves
                // (after the new incoming SDK call is live) — same lifecycle
                // as the Hold/Merge paths use below.
                callWaitingTransitionRef.current = true;
            }
            // Pass the incoming caller's name so the dialer can show "you
            // just talked to <name>" in the post-call disposition view
            // instead of the literal fallback "Lead". The multi-line
            // session's `lastActiveLeadId` lookup misses the incoming
            // caller (they're not in the queue), so without this detail
            // the disposition card shows generic "Lead" after end-call.
            const pauseDetail = {
                incomingCallerName: callDetails.callerName,
                incomingCallerNumber: callDetails.callerNumber,
                incomingLeadId: callDetails.leadId,
            };
            if (isInMultiLineModeRef.current) {
                window.dispatchEvent(new CustomEvent('multiline:emergency-pause', { detail: pauseDetail }));
                toast({
                    title: "Dialer paused",
                    description: "Multi-line is paused — resume after this call ends",
                });
            } else if (isInPowerDialerModeRef.current) {
                window.dispatchEvent(new CustomEvent('powerdialer:emergency-pause', { detail: pauseDetail }));
                toast({
                    title: "Dialer paused",
                    description: "Power dialer is paused — resume after this call ends",
                });
            }
        }

        // CALL-WAITING HANDLING: if user was already on an active call, either put it
        // on hold (Hold & Answer) or merge it into the new conference (Merge).
        const priorCallSid = activeCallSidRef.current;
        if (priorCallSid && (options?.holdActive || options?.mergeActive)) {
            // Lock auto-resume for the duration of the handoff. Without this,
            // the 300-500ms window where `activeCallSid` is null but `heldCall`
            // is set would trigger a false auto-resume and drop the new call.
            callWaitingTransitionRef.current = true;
            // Signal to background pollers / batch-progression code that
            // call-waiting is active — they should NOT end the session or
            // dial new batches while we're mid-handoff. Cleared when the
            // held call is resumed/cleared (see resumeHeldCall + clearHeldCall).
            try { sessionStorage.setItem('__callwaiting_active', '1'); } catch {}
            // Capture prior-call metadata BEFORE connect() overwrites it.
            const priorMetadata = callMetadata;
            // Conference naming varies by dialer:
            //   - manual/power-dialer outbound: `call-{sid}`
            //   - incoming answered:            `incoming-{parentCallSid}`
            //   - multi-line race-to-answer:   `race-{sessionId}`
            // Prefer the authoritative `currentConferenceName` the
            // TwilioDeviceContext tracks (set by PowerDialerSession /
            // MultiLineDialerSession via setActiveConference). Fall back
            // to the derived pattern so pre-refactor single-line calls
            // still work.
            const priorConferenceName = currentConferenceName
                || (priorMetadata?.parentCallSid
                    ? `incoming-${priorMetadata.parentCallSid}`
                    : `call-${priorCallSid}`);
            const priorName = priorMetadata?.leadName || 'prior call';

            if (options.holdActive) {
                // AWAIT the hold before connecting to the new conference. Fire-and-forget
                // races with connect() — agent's SDK leaves conference A before Twilio has
                // placed customer A on hold, so conference A collapses and customer A gets
                // dropped instead of cleanly held. That's the "tweaking" symptom.
                try {
                    const holdResp = await api.post<{ data?: { skipped?: boolean } }>({
                        url: getApiUrl(`/communication/calls/${priorCallSid}/hold`),
                        body: { onHold: true, conferenceName: priorConferenceName || undefined },
                    });
                    // Backend returns skipped=true when the prior call's
                    // customer hadn't joined the conference yet (agent pressed
                    // Hold & Answer during the outbound ringing phase). In
                    // that case the original call is effectively abandoned —
                    // there's no customer to hold and nothing to resume to.
                    // Skip the heldCall state so auto-resume doesn't try to
                    // reconnect to a dead conference after incoming ends.
                    const skipped = (holdResp?.data as any)?.data?.skipped === true;
                    if (skipped) {
                        // Release the transition guard so the new incoming
                        // call's connect+accept path runs normally. No held
                        // state to track.
                        callWaitingTransitionRef.current = false;
                        try { sessionStorage.removeItem('__callwaiting_active'); } catch {}
                        setIsMergedCall(false);
                    } else {
                        toast({
                            title: "Call on Hold",
                            description: `${priorName} placed on hold`,
                        });
                        setHeldCall({
                            callSid: priorCallSid,
                            conferenceName: priorConferenceName,
                            callerName: priorName,
                            callerNumber: priorMetadata?.phoneNumber || '',
                            leadId: priorMetadata?.leadId,
                            heldAt: new Date(),
                            fromMultiLine: answerWasMultiLine,
                        });
                        setIsMergedCall(false);
                    }
                } catch (err) {
                    console.warn('[CallWaiting] Failed to place active call on hold:', err);
                    toast({
                        title: "Hold Failed",
                        description: "Could not place the previous call on hold. Staying on current call.",
                        variant: "destructive",
                    });
                    // Release the transition guard — if we don't, auto-resume
                    // stays permanently disabled and the held call can never be
                    // reconnected automatically.
                    callWaitingTransitionRef.current = false;
                    // Don't connect to the new conference if hold failed — agent stays
                    // on the original call rather than entering a half-broken state.
                    return;
                }
            } else if (options.mergeActive) {
                // FLIPPED MERGE: pull the INCOMING customer into the ACTIVE (prior)
                // conference — NOT the other way around. This keeps Caius (or
                // whichever active customer you're on) as the "main" call in the
                // UI; the incoming caller slides into the Add Party list below.
                //
                // Agent never switches SDK calls — we don't call connect(). The
                // agent's current leg stays in `priorConferenceName` and the
                // backend redirects the incoming's customer leg via
                // twilio.calls(incomingParentSid).update({twiml: <Conference>}).
                try {
                    await api.post({
                        url: getApiUrl(`/communication/calls/merge`),
                        body: {
                            // Pass the incoming's parent SID directly — for
                            // inbound calls this IS the customer leg, so the
                            // backend can skip its customer-lookup logic.
                            customerCallSid: callDetails.callSid,
                            targetConferenceName: priorConferenceName,
                        },
                    });
                    toast({
                        title: "Calls Merged",
                        description: "You're now in a 3-way call",
                    });
                    setIsMergedCall(true);

                    // Safety net — refresh Add Party list THREE times after merge.
                    // The redirected customer leg takes 1-3s to land in the target
                    // conference depending on Twilio's own processing. Three
                    // staggered retries cover common (1.5s), typical (3s), and
                    // slow (5s) cases. currentConferenceName is already set to
                    // `priorConferenceName` (the active conf the agent is on), so
                    // refreshConferenceParticipants() queries the right conf.
                    [1500, 3000, 5000].forEach(delay => {
                        setTimeout(() => {
                            refreshConferenceParticipants().catch((e) => {
                                console.warn(`[CallWaiting] participant refresh after flipped merge (${delay}ms) failed:`, e);
                            });
                        }, delay);
                    });

                    // Handoff is complete — agent never left the prior call.
                    // Release the transition guard immediately so auto-resume
                    // works normally when this merged call eventually ends.
                    callWaitingTransitionRef.current = false;
                    try { sessionStorage.removeItem('__callwaiting_active'); } catch {}

                    // IMPORTANT: do NOT fall through to the connect() block
                    // below. Agent stays on their current SDK call.
                    return;
                } catch (err) {
                    console.warn('[CallWaiting] Failed to merge incoming into active conference:', err);
                    toast({
                        title: "Merge Failed",
                        description: "Could not merge calls",
                        variant: "destructive",
                    });
                    callWaitingTransitionRef.current = false;
                    try { sessionStorage.removeItem('__callwaiting_active'); } catch {}
                    // Don't connect if merge failed — agent stays on the
                    // original call; the incoming caller is still alone in
                    // their own conference and will hang up / be auto-cleaned.
                    return;
                }
            }
        }

            // Connect to the conference using TwilioDevice
            // Pass a special "conference:" prefix to indicate this is a conference join request
            // The backend TwiML app webhook will detect this and generate Conference TwiML
            const joinedCall = await connect({
                to: `conference:${callDetails.conferenceName}`,
                leadId: callDetails.leadId,
                metadata: {
                    isConference: "true",
                    conferenceName: callDetails.conferenceName,
                    parentCallSid: callDetails.callSid,
                    leadName: callDetails.callerName,
                    phoneNumber: callDetails.callerNumber,
                },
            });

            // CRITICAL (Mark Gurraj 2026-07-22): connect() catches its OWN
            // errors internally and returns null instead of throwing. Without
            // this check, a failed join (mic permission, token, network) fell
            // through as "success": the agent's multi-line race leg was already
            // hard-disconnected by connect()'s SDK swap, the guard got cleared,
            // and the UI toasted "Call Answered" with NO live call — dead air,
            // frozen paused dialer, incoming caller rings out to no_answer,
            // agent force-restarts sessions over and over ("CRM freaks out").
            // Throwing here routes into the catch below, which resumes the
            // dialer and resets every guard.
            if (!joinedCall) {
                throw new Error('Could not join the incoming call — the line may have already hung up');
            }

            // Register the conference name with TwilioDevice so Add Party /
            // refreshConferenceParticipants queries the RIGHT conference.
            // Without this, currentConferenceName stays null and the refresh
            // falls back to `call-{agentSid}` which doesn't exist for
            // incoming-call or merged conferences — Add Party shows empty.
            setActiveConference(callDetails.conferenceName);

            // Handoff complete. The new call is alive; release the guard so
            // auto-resume can fire when this new call later ends.
            callWaitingTransitionRef.current = false;

            // Track if we should navigate to lead details
            let shouldNavigateToLead = false;

            // If this is a pooled lead, auto-assign it to this agent
            if (callDetails.isPooledLead && callDetails.leadId) {
                try {
                    const assignResponse = await api.post({
                        url: getApiUrl('/lead-assignment/pool-assign'),
                        body: {
                            leadId: callDetails.leadId,
                            callSid: callDetails.callSid,
                            // Live Transfer answers take over even pre-assigned
                            // leads (mortgage funnels pre-assign to the API
                            // key's owner). Server re-verifies this flag
                            // against the campaign ring log before honoring.
                            allowReassign: callDetails.isLiveTransfer === true,
                        },
                        auth: true,
                    });

                    const responseData = assignResponse.data as { success?: boolean; data?: { success?: boolean }; statusCode?: number; message?: string };
                    if (responseData?.data?.success || responseData?.statusCode === 200) {
                        toast({
                            title: "Lead Assigned",
                            description: `${callDetails.callerName} has been assigned to you`,
                        });
                        // Navigate to lead details after successful pool assignment
                        shouldNavigateToLead = true;
                    }
                } catch (assignError: any) {
                    // Lead may have been assigned to another agent - still continue with call
                    if (assignError?.response?.status === 409) {
                        toast({
                            title: "Lead Already Assigned",
                            description: "This lead was assigned to another agent",
                        });
                    } else {
                        toast({
                            title: "Assignment Failed",
                            description: assignError?.response?.data?.message || "Could not assign lead",
                            variant: "destructive",
                        });
                    }
                }
            }

            toast({
                title: "Call Answered",
                description: `Connected to ${callDetails.callerName}`,
            });

            // Auto-open lead details if:
            // 1. We have a leadId AND
            // 2. Either it's a pooled lead that was just assigned, OR auto-open setting is enabled for regular leads
            if (callDetails.leadId) {
                const autoOpenEnabled = typeof window !== 'undefined'
                    ? localStorage.getItem(AUTO_OPEN_LEAD_DETAILS_KEY) === 'true'
                    : false;

                // Navigate if: pooled lead was successfully assigned OR auto-open is enabled for regular leads
                if (shouldNavigateToLead || (!callDetails.isPooledLead && autoOpenEnabled)) {
                    // Small delay to ensure backend has processed the call and lead data is accessible
                    // This prevents "can't find lead" errors when navigating too quickly
                    setTimeout(() => {
                        router.push(`/leads/${callDetails.leadId}`);
                    }, 500);
                }
            }
        } catch (error: any) {
            console.error('[IncomingCall] answerCall connect failed:', error);
            // Reset call-waiting visual state so UI doesn't lie.
            setIsMergedCall(false);
            // Release the transition guard so auto-resume can reconnect the
            // agent to the held call (the original call is still alive on
            // hold music — don't strand the customer).
            callWaitingTransitionRef.current = false;
            // Clear the call-waiting sessionStorage flag too — without this the
            // flag stays set forever, leaving the Active Call UI stuck on a
            // dead call and preventing multi-line/power-dialer from resuming
            // normally on next user action.
            try { sessionStorage.removeItem('__callwaiting_active'); } catch {}
            // Wake the dialer back up so the agent isn't stranded on a frozen
            // session. Same dispatch the call-end effect uses.
            window.dispatchEvent(new Event('multiline:emergency-resume'));
            window.dispatchEvent(new Event('powerdialer:emergency-resume'));
            toast({
                title: "Failed to Answer Call",
                description: error?.message || "An error occurred while answering the call",
                variant: "destructive",
            });
        }
    }, [currentCall, connect, hideIncomingCall, stopRingtone, toast, router, callMetadata]);

    const declineCall = useCallback(async (callId: string) => {
        if (!currentCall) {
            return;
        }

        // Capture call SID before hiding (since hideIncomingCall clears currentCall)
        const callSid = currentCall.callSid;
        const isLiveTransfer = !!currentCall.isLiveTransfer;

        // Mark this call as handled so it won't be re-shown if broadcast arrives again
        handledCallSidsRef.current.add(callSid);
        // Prevent unbounded growth — keep only the most recent 50 entries
        if (handledCallSidsRef.current.size > 100) {
            const entries = Array.from(handledCallSidsRef.current);
            handledCallSidsRef.current = new Set(entries.slice(-50));
        }

        // Stop ringtone and hide the widget IMMEDIATELY when user clicks decline
        // Don't wait for API call - user has made their decision
        stopRingtone();
        hideIncomingCall();

        try {
            // Make API call to backend to decline the call
            const declineRes = await api.post({
                url: getApiUrl(`/api/communication/calls/${callSid}/decline`),
                auth: true,
            });
            let d: any = declineRes?.data;
            for (let i = 0; i < 3 && d && typeof d === 'object' && 'data' in d; i++) d = d.data;

            toast({
                title: "Call Declined",
                // A live transfer keeps ringing the rest of the group; only this agent is out.
                // Once everyone has declined, the caller goes straight to voicemail.
                description: d?.sentToVoicemail
                    ? "Everyone declined, so the caller went to voicemail."
                    : isLiveTransfer
                    ? "The rest of your team is still ringing."
                    : "The incoming call has been declined",
            });
        } catch (error: any) {
            toast({
                title: "Failed to Decline Call",
                description: error?.message || "An error occurred while declining the call",
                variant: "destructive",
            });
        }
    }, [currentCall, hideIncomingCall, stopRingtone, toast]);

    const dismissCall = useCallback(() => {
        // Mark as handled if there's a current call to prevent re-showing
        if (currentCall?.callSid) {
            handledCallSidsRef.current.add(currentCall.callSid);
            // Prevent unbounded growth — keep only the most recent 50 entries
            if (handledCallSidsRef.current.size > 100) {
                const entries = Array.from(handledCallSidsRef.current);
                handledCallSidsRef.current = new Set(entries.slice(-50));
            }
        }
        hideIncomingCall();
    }, [currentCall, hideIncomingCall]);

    const clearHeldCall = useCallback(() => {
        setHeldCall(null);
        try { sessionStorage.removeItem('__callwaiting_active'); } catch {}
    }, []);

    /**
     * Swap calls: put current call on hold, disconnect agent device, resume the
     * previously-held call, and rejoin its conference.
     *
     * The new "current" becomes the held one; the former current goes into the
     * held slot so the agent can swap back again.
     */
    // FROZEN-UI FIX (2026-07-23, multi-line Hold & Answer): if the HELD call
    // died while on hold (customer hung up, or the agent ended it from the
    // multi-line card — whose line sid differs from heldCall.callSid, so the
    // call_ended broadcast never matched), auto-resume reconnected the agent
    // into a DEAD/empty conference: silent room, timer running, dialer stuck
    // paused — "all frozen". Verify liveness FIRST and bail out cleanly.
    const abandonHeldCall = useCallback((reason: string) => {
        setHeldCall(null);
        setIsMergedCall(false);
        try { sessionStorage.removeItem('__callwaiting_active'); } catch { /* noop */ }
        // Wake whichever dialer we emergency-paused (handlers self-guard).
        window.dispatchEvent(new Event('multiline:emergency-resume'));
        window.dispatchEvent(new Event('powerdialer:emergency-resume'));
        toast({
            title: "Held call ended",
            description: reason,
        });
    }, [toast]);

    const resumeHeldCall = useCallback(async () => {
        const held = heldCall;
        if (!held) return;
        // Read from the HELD RECORD (stamped at hold time) — the mode refs are
        // long cleared by the incoming call's connect() at this point, so
        // reading them here always said 'manual' (the stubborn overlay bug).
        const wasMultiLineHeld = held.fromMultiLine === true || isInMultiLineModeRef.current;

        // Liveness check — never reconnect into a dead conference.
        // v2 (2026-07-24): checking the held CALL SID was wrong — that's the
        // agent's old SDK leg, which always reads 'completed' after answering
        // the incoming, so every resume got abandoned and the customer sat on
        // hold forever ("mute doesn't work, still on hold, can't do shit").
        // The truthful liveness subject is the CONFERENCE the customer waits
        // in: live-with-participants = resumable; the endpoint fails OPEN so a
        // Twilio blip can't abandon a real call.
        try {
            const res: any = await api.get({
                url: getApiUrl(`/communication/conference-live?name=${encodeURIComponent(held.conferenceName)}`),
                auth: true,
            });
            const d = res?.data?.data ?? res?.data ?? null;
            if (d && d.live === false) {
                abandonHeldCall(`${held.callerName} is no longer on the line.`);
                return;
            }
        } catch { /* liveness unknown — proceed with the normal resume attempt */ }

        const currentSid = activeCallSidRef.current;
        const currentMeta = callMetadata;
        const currentConferenceName = currentMeta?.parentCallSid
            ? `incoming-${currentMeta.parentCallSid}`
            : currentSid
                ? `call-${currentSid}`
                : null;

        // BELT-AND-SUSPENDERS: re-dispatch emergency-pause events to keep any
        // underlying dialer session FROZEN throughout the resume. Auto-resume
        // happens after a call-waiting swap ends — the power/multi-line
        // session should already be paused from the original emergency-pause,
        // but the intervening SDK disconnect/reconnect cycle can incidentally
        // reset internal refs. Re-firing the event guarantees isPausedRef
        // stays true so the dialer can't auto-advance/retry once the agent
        // reconnects to the held customer and then ends that call.
        if (isInMultiLineModeRef.current) {
            window.dispatchEvent(new Event('multiline:emergency-pause'));
        } else if (isInPowerDialerModeRef.current) {
            window.dispatchEvent(new Event('powerdialer:emergency-pause'));
        }

        try {
            // 1. Hold the current call (if any) so its customer hears hold music.
            if (currentSid) {
                await api.post({
                    url: getApiUrl(`/communication/calls/${currentSid}/hold`),
                    body: { onHold: true, conferenceName: currentConferenceName || undefined },
                }).catch((err) => {
                    console.warn('[CallWaiting] Failed to hold current call during resume:', err);
                });
            }

            // 2. Disconnect agent's device from the current conference.
            disconnect();

            // 3. Resume the held call server-side (unhold its customer leg).
            // conferenceName (2026-07-24): on 2-instance prod the sid→conference
            // cache lives on the OTHER instance half the time and the old leg
            // is gone from the room — the name lets any instance resolve it.
            await api.post({
                url: getApiUrl(`/communication/calls/${held.callSid}/resume`),
                body: { conferenceName: held.conferenceName },
            }).catch((err) => {
                // Not fatal — the customer may already be off hold if Twilio state drifted.
                console.warn('[CallWaiting] Resume endpoint returned error:', err);
            });

            // 4. Reconnect agent's Twilio device to the held conference.
            // isMultiLine (2026-07-24): without it, connect() classified the
            // resumed call as 'manual' → activeCallSource 'manual' → the
            // widget's Active Call OVERLAY covered the (alive, paused)
            // multi-line session view — the "resumes in manual UI" bug. With
            // it, connect() restores ML mode itself and the widget keeps the
            // session view in front.
            await connect({
                to: `conference:${held.conferenceName}`,
                leadId: held.leadId,
                metadata: {
                    isConference: "true",
                    conferenceName: held.conferenceName,
                    parentCallSid: held.callSid,
                    leadName: held.callerName,
                    phoneNumber: held.callerNumber,
                    ...(wasMultiLineHeld ? { isMultiLine: true } : {}),
                } as any,
            });

            // After agent successfully rejoins the held conference, re-pause the
            // dialer AGAIN. The connect() above may have cleared isInPowerDialerMode
            // / isInMultiLineMode refs (because it detected a non-dialer call with
            // an active dialer session). Re-dispatching emergency-pause is a no-op
            // if already paused, but ensures we're guaranteed paused.
            if (isInMultiLineModeRef.current) {
                window.dispatchEvent(new Event('multiline:emergency-pause'));
            } else if (isInPowerDialerModeRef.current) {
                window.dispatchEvent(new Event('powerdialer:emergency-pause'));
            }

            // MULTI-LINE UI HANDBACK (2026-07-24): resuming a held MULTI-LINE
            // call previously left the widget in the generic call-waiting view
            // ("manual UI") because __callwaiting_active stays set and connect()
            // cleared the ML mode flag — and ending from that view tore the
            // whole session down (dev logs 00:47: session /complete + /end).
            // Hand the call back to the session UI: re-assert ML mode + the
            // conference, and clear the call-waiting flag — the session is
            // still emergency-paused (awaitingMultiLineDisposition), so batch
            // auto-advance stays blocked and End behaves like a normal
            // line-end with the session left paused/resumable.
            if (wasMultiLineHeld) {
                try { setIsInMultiLineMode(true); } catch { /* setter optional */ }
                try { setActiveConference(held.conferenceName); } catch { /* best effort */ }
                try { sessionStorage.removeItem('__callwaiting_active'); } catch { /* noop */ }
                window.dispatchEvent(new Event('multiline:emergency-pause'));
            }

            // 5. Swap state: the former current call becomes the newly-held call.
            if (currentSid && currentConferenceName) {
                setHeldCall({
                    callSid: currentSid,
                    conferenceName: currentConferenceName,
                    callerName: currentMeta?.leadName || 'previous call',
                    callerNumber: currentMeta?.phoneNumber || '',
                    leadId: currentMeta?.leadId,
                    heldAt: new Date(),
                });
            } else {
                setHeldCall(null);
            }
            // Merge state doesn't carry across a swap.
            setIsMergedCall(false);
            // NOTE: We intentionally do NOT clear __callwaiting_active here.
            // The resumed call is about to play out and when IT ends, the
            // dialer's auto-advance/retry logic will try to fire. We keep the
            // flag SET so those paths stay blocked until the resumed call
            // actually ends. The flag is cleared by the activeCallSid→null
            // effect below (when the resumed call dies + no held call left)
            // or when the user explicitly resumes the dialer.

            toast({
                title: "Call Resumed",
                description: `Resumed call with ${held.callerName}`,
            });
        } catch (error: any) {
            toast({
                title: "Resume Failed",
                description: error?.message || "Could not resume the held call",
                variant: "destructive",
            });
            // Never leave the agent stranded frozen: clear the held state and
            // wake the paused dialer so the session is usable again.
            abandonHeldCall("Couldn't reconnect — the dialer has been unfrozen.");
        }
    }, [heldCall, callMetadata, connect, disconnect, toast, abandonHeldCall]);

    // Guard: prevents auto-resume from hijacking the Hold & Answer / Merge
    // transition. When agent presses Hold & Answer, their SDK disconnects
    // from the old conference for ~300-500ms while the new call connects.
    // During that window `activeCallSid` is null AND `heldCall` exists, which
    // falsely matches the auto-resume condition. Without this guard, the
    // held call gets resumed right back and the incoming call is orphaned.
    const callWaitingTransitionRef = useRef(false);

    // Auto-resume: if the active call ends while we have a held call, automatically
    // swap back so the agent isn't stranded with an idle device and a customer on hold.
    // We detect "active call ended" by watching activeCallSid transition to null.
    const prevActiveCallSidRef = useRef<string | null>(null);
    // RE-ENTRANCY GUARD: resumeHeldCall is ASYNC — it awaits /hold, /resume, then
    // connect(). Inside connect() the old SDK leg disconnects briefly, which
    // flips activeCallSid back to null while `heldCall` is still set (heldCall
    // only clears AFTER the whole resumeHeldCall completes). Without this guard,
    // the auto-resume effect re-fires mid-flight and queues a SECOND
    // resumeHeldCall → two /resume POSTs + two connect() cycles → the second
    // connect() disconnects the first agent SDK leg which (with eConExit=true
    // from the earlier resume) collapses the conference. Symptom: "goes back
    // to Caius for 1-2s then ends and glitches out". Logs at 05:44:49–05:44:52
    // showed the duplicate /resume followed by 404 on the collapsed conf.
    const resumeInFlightRef = useRef(false);
    useEffect(() => {
        const prev = prevActiveCallSidRef.current;
        prevActiveCallSidRef.current = activeCallSid;

        // CRITICAL: skip the entire end-of-call cleanup (flag clear,
        // emergency-resume dispatch, auto-resume held call) when we're mid
        // SDK swap during acceptCall. The OLD SDK leg disconnects briefly
        // while connect() establishes the NEW leg → activeCallSid flips
        // null then back to the new SID. If we let the cleanup run during
        // that null window we wrongly clear __callwaiting_active and
        // dispatch emergency-resume → multi-line un-pauses → dialNextLeads
        // fires disconnect() → cancel POST `agent_hangup` kills the new
        // incoming call (verified in dev render logs, 22:27:00.731).
        //
        // The ref is cleared at the bottom of acceptCall once connect()
        // resolves, so any FUTURE activeCallSid → null transitions (the
        // real call ending) run the cleanup normally.
        if (callWaitingTransitionRef.current) {
            return;
        }

        // Skip if another resumeHeldCall is already in flight — prevents the
        // mid-connect() re-entry described above.
        if (resumeInFlightRef.current) {
            return;
        }

        // Active call ended: clear merge state and supervisor coach banner
        // regardless. The supervisor's session terminates with the call
        // (Twilio drops the Media Stream when the call ends), so leaving
        // the banner up would be a lie.
        if (prev && !activeCallSid) {
            setIsMergedCall(false);
            setCoachActive(null);
            // Clear the call-waiting guard flag IFF no held call remains.
            // - Merge path: no heldCall → clear immediately on active call end.
            // - Hold path: heldCall present → DON'T clear (auto-resume about
            //   to fire); wait for the resumed call to end. When the resumed
            //   call eventually ends, heldCall will have been cleared by
            //   resumeHeldCall and we'll hit this branch again cleanly.
            if (!heldCall) {
                // Read-and-clear the flag atomically so we only dispatch
                // emergency-resume when we were ACTUALLY in a Hold & Answer
                // / Merge cycle. Without this gate, emergency-resume fires
                // on every normal call end — breaks the power dialer 60s
                // disposition pause because the power dialer's handler
                // clears awaitingDispositionRef on any emergency-resume.
                let wasCallWaiting = false;
                try {
                    wasCallWaiting = sessionStorage.getItem('__callwaiting_active') === '1';
                    if (wasCallWaiting) sessionStorage.removeItem('__callwaiting_active');
                } catch {}
                if (wasCallWaiting) {
                    // Wake the dialer back up. During Hold & Answer the dialer
                    // was emergency-paused; without this resume it stays paused
                    // and when the user presses Resume the session re-dials the
                    // CURRENT lead instead of advancing. Dispatch BOTH variants
                    // — listeners each guard with `emergencyPausedByUsRef`, so
                    // only the dialer we actually paused responds.
                    window.dispatchEvent(new Event('multiline:emergency-resume'));
                    window.dispatchEvent(new Event('powerdialer:emergency-resume'));
                }
            }
        }

        // Transition: had active call, now none, and a held call still exists.
        if (prev && !activeCallSid && heldCall) {
            toast({
                title: "Resuming Held Call",
                description: `Reconnecting to ${heldCall.callerName}`,
            });
            resumeInFlightRef.current = true;
            // Defer to next tick so disconnect cleanup finishes first.
            setTimeout(() => {
                resumeHeldCall().finally(() => {
                    resumeInFlightRef.current = false;
                });
            }, 250);
        }
    }, [activeCallSid, heldCall, resumeHeldCall, toast]);

    // If the HELD call drops server-side (customer hung up while on hold), clear it.
    // Listener is wired into the existing `call_ended` broadcast handler below via a ref.
    const heldCallSidRef = useRef<string | null>(null);
    useEffect(() => {
        heldCallSidRef.current = heldCall?.callSid || null;
    }, [heldCall]);

    // Held-call teardown, driven from the MAIN call_ended handler (see below) rather
    // than by attaching a second .on() to an already-subscribed channel.
    const heldCallEndedRef = useRef<(callSid: string) => void>(() => {});

    // Keep the held-call teardown behaviour current. The MAIN call_ended handler calls
    // heldCallEndedRef.current(callSid) — no second listener is attached to the channel,
    // so nothing depends on late .on() registration working.
    useEffect(() => {
        heldCallEndedRef.current = (callSid: string) => {
            if (callSid && heldCallSidRef.current === callSid) {
                toast({
                    title: "Held Call Ended",
                    description: `${heldCall?.callerName || 'The held call'} hung up`,
                });
                setHeldCall(null);
            }
        };
    }, [heldCall, toast]);

    const value: IncomingCallContextType = {
        currentCall,
        isCallVisible,
        // isCallWaiting = incoming arrived while user was already on an ACTIVE,
        // connected call (customer has answered — callStatus === 'in-progress').
        // During outbound ringing (agent dialing but customer hasn't picked up),
        // there's no conversation to hold, so show the regular incoming UI
        // (Answer / Decline) instead of Hold & Answer / Merge. Prevents the
        // "hold during ring" bug where agents hit Hold & Answer before the
        // customer joined the conference and backend errored out.
        // MULTI-LINE (2026-07-23): the device context's callStatus is NOT
        // 'in-progress' during a multi-line session (line states live in the
        // session component), so agents mid-conversation got the plain
        // Answer/Decline card — answering yanked them out with no hold/pause.
        // In multi-line mode with the agent's conference leg up, the
        // call-waiting UI (Hold current & answer / Merge / Decline) is ALWAYS
        // the right one: Hold & Answer emergency-pauses the session and
        // re-queues ringing lines even when no customer is connected yet.
        isCallWaiting: !!(isCallVisible && activeCallSid && (callStatus === 'in-progress' || isInMultiLineMode)),
        heldCall,
        isMergedCall,
        coachActive,
        showIncomingCall,
        hideIncomingCall,
        answerCall,
        declineCall,
        dismissCall,
        resumeHeldCall,
        clearHeldCall,
    };

    return (
        <IncomingCallContext.Provider value={value}>
            {children}
        </IncomingCallContext.Provider>
    );
};

export const useIncomingCall = (): IncomingCallContextType => {
    const context = useContext(IncomingCallContext);
    if (context === undefined) {
        throw new Error("useIncomingCall must be used within an IncomingCallProvider");
    }
    return context;
};

export default IncomingCallProvider;
